import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

/**
 * Component to display user credit transaction history.
 */
export default function CreditTransactionLog() {
    const { getAccessTokenSilently } = useAuth0();
    const [transactions, setTransactions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const LIMIT = 20;

    const fetchTransactions = useCallback(async (currentOffset = 0) => {
        setIsLoading(true);
        try {
            const token = await getAccessTokenSilently();
            const response = await fetch(`/api/v1/users/me/credits/history?limit=${LIMIT}&offset=${currentOffset}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch credit history');
            }

            const fetchedTransactions = await response.json();

            if (currentOffset === 0) {
                setTransactions(fetchedTransactions);
            } else {
                setTransactions(prev => [...prev, ...fetchedTransactions]);
            }

            setHasMore(fetchedTransactions.length === LIMIT);
        } catch (err) {
            console.error('Failed to fetch credit history:', err);
        } finally {
            setIsLoading(false);
        }
    }, [getAccessTokenSilently]);

    useEffect(() => {
        fetchTransactions(0);
    }, [fetchTransactions]);

    const loadMore = () => {
        const nextOffset = offset + LIMIT;
        setOffset(nextOffset);
        fetchTransactions(nextOffset);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    };

    const formatReason = (reason) => {
        const reasons = {
            signup_bonus: 'Signup Bonus',
            subscription: 'Subscription Credits',
            purchase: 'Credit Purchase',
            download: 'Icon Download',
            refund: 'Refund',
            admin: 'Admin Adjustment'
        };
        return reasons[reason] || reason.charAt(0).toUpperCase() + reason.slice(1).replace(/_/g, ' ');
    };

    return (
        <section id="credit-history">
            {isLoading && transactions.length === 0 ? (
                <div className="glass p-8 text-center">
                    <div className="animate-spin w-8 h-8 border-2 border-violet-400 border-t-transparent rounded-full mx-auto mb-3" />
                    <p className="text-slate-600 dark:text-slate-400">Loading history...</p>
                </div>
            ) : transactions.length === 0 ? (
                <div className="glass p-8 text-center">
                    <p className="text-slate-500 dark:text-slate-400">No transactions yet.</p>
                </div>
            ) : (
                <div className="overflow-hidden glass">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-slate-200 dark:border-white/5 bg-slate-50/50 dark:bg-white/5">
                                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Reason</th>
                                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Amount</th>
                                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Balance</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 dark:divide-white/5">
                                {transactions.map((tx) => (
                                    <tr key={tx.id} className="hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors">
                                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
                                            {formatDate(tx.createdAt)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-slate-900 dark:text-slate-100">
                                            {formatReason(tx.reason)}
                                        </td>
                                        <td className={`px-4 py-3 text-sm font-medium text-right whitespace-nowrap ${tx.amount > 0 ? 'text-emerald-500' : 'text-slate-600 dark:text-slate-400'}`}>
                                            {tx.amount > 0 ? `+${tx.amount}` : tx.amount}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 text-right tabular-nums">
                                            {tx.balanceAfter}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {hasMore && (
                        <div className="p-4 border-t border-slate-200 dark:border-white/5 text-center">
                            <button
                                onClick={loadMore}
                                disabled={isLoading}
                                className="btn-secondary py-1.5 px-6 text-sm"
                            >
                                {isLoading ? 'Loading...' : 'Load More'}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}
